import { defineStore } from 'pinia';
import {
  EPremiumFeature,
  ERoleIdentifier,
  EUserFacilityRelationType,
  hasUserRole,
} from '@sediwork/shared';
import type { TFacilityTree } from '@sediwork/shared';
import type { TStoreUser } from '../types/user';

type TMainStore = {
  user: TStoreUser
  facilityTree: TFacilityTree | null
  specialities: TSpecialities
};

export const useMainStore = defineStore('main', {
  state: (): TMainStore => ({
    user: {},
    facilityTree: null,
    specialities: [],
  }),
  getters: {
    hasRole({ user }): (roles: ERoleIdentifier | ReadonlyArray<ERoleIdentifier>) => boolean {
      return (roles) => hasUserRole(user, roles);
    },
    isNonAdminFacilityPlanner: ({ user }) =>
      user.role?.identifier === ERoleIdentifier.ADMINISTRATIVE_STAFF
      && !user.main_facility_admin
      && (user.facilities?.some((f) => f.type === EUserFacilityRelationType.ADMINISTRATE)
        ?? false),
    isAdministrativeStaff: ({ user }) =>
      user.role?.identifier === ERoleIdentifier.ADMINISTRATIVE_STAFF,
    isReadOnly: ({ user }) => user.role?.identifier === ERoleIdentifier.READ_ONLY,
    isRotatingStaff: ({ user }) => user.role?.identifier === ERoleIdentifier.ROTATING_STAFF,
    hasNetworkCompetencesAccess: ({ user }) =>
      user.role?.identifier === ERoleIdentifier.ADMINISTRATIVE_STAFF
      && !!user.main_facility_admin
      && !!user.main_facility?.premium_features?.includes(EPremiumFeature.NETWORK_COMPETENCES),
    isClinicStructureReadOnly(): boolean {
      return this.isReadOnly || this.isNonAdminFacilityPlanner;
    },
    hasAdministrativeAccess(): boolean {
      return this.hasRole(administrativeAccessRoles);
    },
    hasQualificationsFeature: ({ user }) =>
      !!user.main_facility?.premium_features?.includes(EPremiumFeature.QUALIFICATIONS),
    hasQualificationsAccess(): boolean {
      return this.hasAdministrativeAccess && this.hasQualificationsFeature;
    },
    isUnrestrictedPlanningGroupAccess: ({ user }) => !!user.main_facility_admin,
    permittedDefaultPlanningGroupIds: ({ user }) => new Set(
      (user.planning_groups ?? [])
        .filter((pg) => pg.type === EUserFacilityRelationType.ADMINISTRATE && pg.planning_group_id)
        .map((pg) => pg.planning_group_id),
    ),
    permittedPlanningGroupIds: ({ user }) => (user.planning_groups ?? [])
      .filter((pg) => pg.planning_group_id)
      .map((pg) => pg.planning_group_id),
    filteredFavoritePlanningGroups: ({ user }) => {
      const favorites = user.favorite_planning_groups ?? [];
      if (user.main_facility_admin) return favorites;
      const ids = (user.planning_groups ?? [])
        .filter((pg) => pg.planning_group_id)
        .map((pg) => pg.planning_group_id);
      return favorites.filter((f) => ids.includes(f.planning_group_id.id));
    },
    permittedUserFacilityIds: ({ user }) => groupFacilityIdsByType(user.facilities),
    isOwnFacility(): (facilityId: string) => boolean {
      return (facilityId) => !this.hasAdministrativeAccess
        || this.permittedUserFacilityIds[EUserFacilityRelationType.ADMINISTRATE].has(facilityId);
    },
    isOwnPlanningGroup(): (planningGroupId: string) => boolean {
      return (planningGroupId) => !this.hasAdministrativeAccess
        || this.isUnrestrictedPlanningGroupAccess
        || this.permittedDefaultPlanningGroupIds.has(planningGroupId);
    },
  },
  actions: {
    setUserContact(user: TStoreUser) {
      this.user.gender = user.gender;
      this.user.title = user.title;
      this.user.first_name = user.first_name;
      this.user.last_name = user.last_name;
      this.user.email = user.email;
    },
    setTfa(tfaSecret: string) {
      this.user.tfa_secret = tfaSecret;
    },
  },
});
